import React from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useTheme } from '../../../../context/ThemeContext';
import { useProfileCompletion } from '../../../../hooks/useProfileCompletion';

const ProfileCompletionTracker = ({ 
  completionPercentage: propCompletionPercentage, 
  missingItems: propMissingItems, 
  onItemClick,
  highlightField 
}) => {
  const { isDarkMode } = useTheme();
  
  // Use hook data if props not provided (for consistency)
  const hookData = useProfileCompletion();
  const completionPercentage = propCompletionPercentage !== undefined ? propCompletionPercentage : hookData.completionPercentage;
  const missingItems = propMissingItems !== undefined ? propMissingItems : hookData.missingFields;

  // Only render when profile is not complete
  if (completionPercentage >= 100) {
    return null;
  }

  const getProgressBarColor = () => {
    if (completionPercentage >= 75) return 'bg-gradient-to-r from-purple-500 to-purple-600';
    if (completionPercentage >= 50) return 'bg-gradient-to-r from-yellow-500 to-orange-500';
    return 'bg-gradient-to-r from-gray-400 to-gray-500';
  };

  const completionItems = [
    {
      id: 'firstName',
      label: 'First Name',
      description: 'Required for professional networking',
      scrollTarget: 'general-info-section'
    },
    {
      id: 'lastName',
      label: 'Last Name', 
      description: 'Complete your professional identity',
      scrollTarget: 'general-info-section'
    },
    {
      id: 'profilePicture',
      label: 'Profile Picture',
      description: 'Add a professional photo',
      scrollTarget: 'profile-picture-section'
    },
    {
      id: 'currentRole',
      label: 'Current Role',
      description: 'Helps others understand your expertise',
      scrollTarget: 'general-info-section'
    },
    {
      id: 'companyName',
      label: 'Company',
      description: 'Provides professional context',
      scrollTarget: 'general-info-section'
    },
    {
      id: 'location',
      label: 'Location',
      description: 'Enables local networking opportunities',
      scrollTarget: 'general-info-section'
    },
    {
      id: 'linkedin',
      label: 'LinkedIn Profile',
      description: 'Essential for professional credibility',
      scrollTarget: 'general-info-section'
    }
  ];

  const handleItemClick = (item) => {
    if (onItemClick) {
      onItemClick(item.scrollTarget);
    }
    
    // Smooth scroll to the section
    const element = document.getElementById(item.scrollTarget);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      
      // Add highlight animation
      element.classList.add('highlight-section');
      setTimeout(() => {
        element.classList.remove('highlight-section');
      }, 2000);
    }
  };

  return (
    <div className="mb-6">
      {/* Compact Progress Bar with Percentage */}
      <div className={`flex items-center gap-3 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Profile Completion
            </span>
            <span className={`text-sm font-bold ${getProgressBarColor().replace('bg-gradient-to-r', 'text').split(' ')[0].replace('bg-', 'text-')}`}>
              {completionPercentage}%
            </span>
          </div>
          <div className={`w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700`}>
            <div 
              className={`h-2 rounded-full transition-all duration-500 ease-out ${getProgressBarColor()}`}
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionTracker; 